import React, { useState, lazy, Suspense } from 'react';

// Hooks
import useCustomerLogin from '@src/hooks/useCustomerLogin';
import useCustomerAccessToken from '@src/hooks/useCustomerAccessToken';

// Comps
const AccountForgotPassword = lazy(
    () => import('@project-stories/ecommerce/Account/AccountForgotPassword/AccountForgotPassword')
);
import {TextField} from '@arctheme-components/elements/TextField/TextField';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@src/stories/elements/ButtonSpinner/ButtonSpinner';
import {Checkbox} from "@project-stories/elements/Checkbox/Checkbox";

const AccountLoginForm = ({ section, blockSettings }: { section: any, blockSettings?: any }) => {
    // Extract settings from block settings or fallback to section
    const settings = blockSettings || section;
    
    const {
        email_label = settings?.email_label || "Email address",
        email_placeholder = settings?.email_placeholder || "Email address", 
        password_label = settings?.password_label || "Password",
        password_placeholder = settings?.password_placeholder || "Password",
        button_label_login = settings?.button_label_login || "Sign in",
        button_style_login = settings?.button_style_login || "primary",
        button_label_forgot_password = settings?.button_label_forgot_password || "Forgot your password?",
        
        // Newsletter signup settings
        show_newsletter_signup = settings?.show_newsletter_signup || false,
        newsletter_label = settings?.newsletter_label || "Sign up for newsletter",
        newsletter_default_checked = settings?.newsletter_default_checked || false,
        newsletter_disclaimer = settings?.newsletter_disclaimer || "",
        
        // Register CTA settings
        show_register_cta = settings?.show_register_cta || false,
        title_register_cta = settings?.title_register_cta || "CREATE AN ACCOUNT",
        tagline_register_cta = settings?.tagline_register_cta || "Create an account for an easy checkout, save addresses and wishlists and view your order history.",
        button_label_register_cta = settings?.button_label_register_cta || "Register",
        button_style_register_cta = settings?.button_style_register_cta || "primary",
        
        // Other settings
        enable_social_login = settings?.enable_social_login || false,
        title_login = settings?.title_login || "Log in to your account",
        tagline_login = settings?.tagline_login || "If you have an account with us, please log in."
    } = section;

    // States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [newsletterOptin, setNewsletterOptin] = useState(newsletter_default_checked || false);

    // Hooks
    const { loginCustomer, loading: loginLoading, error, success } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Check if the pair of email and password exists in the system
        const data: any = await getAccessToken(email, password);

        // If exists, then sign in
        if (data.success) {
            await loginCustomer(email, password, newsletterOptin);
        }
    };

    const toggleForgotPassword = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setShowForgotPassword(!showForgotPassword);
    };

    const showSuccess = false;

    return (
        <>
            {!showForgotPassword && (
                <form
                    data-shopify-captcha='true'
                    id='loginForm'
                    onSubmit={handleSubmit}
                    className='account-page__form'
                >
                    <div className='flex flex-col gap-y-4 w-full'>
                
                        {(error || accessError) && (
                            <Paragraph
                                text={error || accessError}
                                type='errors'
                            />
                        )}

                        <input
                            className={`w-full`}
                            type='email'
                            id='emailLogin'
                            name='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required={true}
                            placeholder={`${email_placeholder}*`}
                        />

                        <input
                            className={`w-full`}
                            type='password'
                            id='passwordLogin'
                            name='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required={true}
                            placeholder={`${password_placeholder}*`}
                        />

                        {show_newsletter_signup && (
                            <Checkbox 
                                id="newsletter-signup"
                                label={newsletter_label || "Sign up for newsletter"} 
                                checked={newsletterOptin}
                                onChange={() => setNewsletterOptin(!newsletterOptin)}
                            />
                        )}

                        {show_newsletter_signup && newsletter_disclaimer && (
                            <p className={`b4 account-page__form-terms text-[#696969]`}>
                                {newsletter_disclaimer}
                            </p>
                        )}

                        {showSuccess && (success || accessSuccess) && (
                            <Paragraph
                                text={success || accessSuccess}
                                type='success text-center'
                            />
                        )}

                        <Link
                            link='#'
                            onClick={toggleForgotPassword}
                            classes='w-full b4 text-left link--underlined text-[#696969]'
                            ariaLabel='Go to reset password page'
                        >
                            {button_label_forgot_password}
                        </Link>

                        <ButtonSpinner
                            isLoading={loginLoading || accessLoading}
                            classes={`button button-${button_style_login} w-full h-[calc(3rem-2px)] text-base`}
                            ariaLabel='Submit login form'
                            loadingText={'Logging in...'}
                        >
                            {button_label_login}
                        </ButtonSpinner>

                        {show_register_cta && (
                            <div className='text-center font-light w-full lg:w-[360px] py-[16px]'>
                                <div className='account-inquiry flex flex-col my-2 text-left space-y-[20px]'>
                                    <h4>{title_register_cta || "CREATE AN ACCOUNT"}</h4>
                                    <span className={`b4`}>
                                        {tagline_register_cta || "Create an account for an easy checkout, save addresses and wishlists and view your order history."}
                                    </span>
                                    <a className={`button button-${button_style_register_cta || 'primary'} w-full`} href='/account/register'>
                                        {button_label_register_cta || "Register"}
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Social login app snippet */}
                        {enable_social_login && <div className='one-click-social-login'></div>}
                    </div>
                </form>
            )}

            {showForgotPassword && (
                <Suspense fallback={<div>Loading...</div>}>
                    <AccountForgotPassword
                        onCancel={toggleForgotPassword}
                        section={section}
                        blockSettings={blockSettings}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AccountLoginForm;
